import { 
  Box, 
  File, 
  FileArchive, 
  FileAudio, 
  FileCode, 
  FileCog, 
  FileDigit, 
  FileImage, 
  FileJson, 
  FileText, 
  FileVideo,
  Globe 
} from 'lucide-react';

/**
 * The mark for one submission's target.
 *
 * `kind` decides first, because it is what the platform recorded at intake. The
 * extension heuristic below can only read a filename, and a URL has no
 * meaningful one - `https://wikipedia.org/` ends in `org/` - so a page was
 * drawn with the same generic sheet of paper as a sample whose type nothing
 * recognised. A URL is not a file that failed to be identified.
 */
export function getFileIcon(
  filename: string | undefined,
  className = "file-icon text-muted",
  kind?: string,
) {
  if (kind === 'url') return <Globe className={className} />;
  if (!filename) return <File className={className} />;
  const ext = filename.split('.').pop()?.toLowerCase();
  
  switch (ext) {
    case 'exe':
    case 'dll':
    case 'msi':
    case 'sys':
    case 'bin':
    case 'elf':
      return <FileDigit className={className} />;
    
    case 'bat':
    case 'cmd':
    case 'ps1':
    case 'sh':
      return <FileCog className={className} />;

    case 'pdf':
    case 'doc':
    case 'docx':
    case 'txt':
    case 'log':
    case 'rtf':
    case 'csv':
      return <FileText className={className} />;

    case 'zip':
    case 'rar':
    case 'tar':
    case 'gz':
    case '7z':
      return <FileArchive className={className} />;

    case 'json':
      return <FileJson className={className} />;

    case 'js':
    case 'jsx':
    case 'ts':
    case 'tsx':
    case 'py':
    case 'c':
    case 'cpp':
    case 'cs':
    case 'html':
    case 'css':
    case 'java':
    case 'go':
    case 'rs':
      return <FileCode className={className} />;

    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
    case 'svg':
    case 'webp':
    case 'bmp':
    case 'ico':
      return <FileImage className={className} />;

    case 'mp4':
    case 'mkv':
    case 'avi':
    case 'webm':
    case 'mov':
      return <FileVideo className={className} />;

    case 'mp3':
    case 'wav':
    case 'ogg':
    case 'flac':
      return <FileAudio className={className} />;

    case 'iso':
    case 'vhd':
    case 'ova':
      return <Box className={className} />;

    default:
      return <File className={className} />;
  }
}
